import {useRef, useCallback, useEffect, memo, MouseEvent} from 'react';

import styled from 'styled-components';
import {Form} from 'react-final-form';

import _FormField from '~/shared/components/FormField';
import {media, flipOnLTR} from '~/shared/theme/utils';
import {getLocalizationService} from '~/shared/services/localisationService';
import {FieldWrapper, FormWrapper as _FormWrapper, FormContentRoot, HeaderText} from '~/shared/components/loginStyled';
import AgreeToTermsSection from '~/shared/components/AgreeToTermsSection';
import {FlexColumn, flexColumn} from '~/shared/theme/FlexLayout';
import {useAutoFocus} from '~/shared/hooks';
import {onTermsOfUseClick, onPrivacyPolicyClick} from '~/common/utils/agreeToTermsUtils';
import {FormValues} from '~/shared/components/SignIn/types';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';
import {validators} from '~/shared/utils/validators';
import PieButton from '~/shared/theme/pie/PieButton';
import {bodyRegular} from '~/shared/theme/typography';

import {checkFreeEmail} from './utils/authHelpers';

const FormWrapper = styled(_FormWrapper)<{alignCenter?: boolean}>`
  ${({alignCenter}) => alignCenter && 'align-items: center;'}
`;

const HeaderAndSubheaderContainer = styled(FlexColumn)`
  justify-content: center;
  margin-bottom: 32px;
  gap: 8px;
`;

const SubheaderText = styled.p`
  ${bodyRegular}
  text-align: center;
`;

const FieldsContainer = styled.div`
  ${flexColumn}

  ${/* sc-selector */ FieldWrapper}:first-child {
    margin-top: 0;
  }

  ${media.minMobile`
    ${FieldWrapper} {
      width: 49%;
      ${flipOnLTR`
        margin-left: 2%;
      `};
      &:nth-child(2) {
        margin-top: 0;
      }
      &:nth-child(2n) {
        ${flipOnLTR`
          margin-left: 0;
        `};
      }
    }
  `};
`;

const Row = styled.div`
  display: flex;
`;

const BrandingPageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  margin-bottom: 12px;
  color: ${({theme}) => theme.colors.secondary};
`;

const BrandingPageSubtitle = styled.h2`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin-bottom: 32px;
  color: ${({theme}) => theme.colors.secondary};
`;

const FormField = styled(_FormField).attrs({
  className: 'white-border-on-contrast sign-up-form-field',
  isPieStyle: true,
})<{addTrailingMargin?: boolean}>`
  ${({addTrailingMargin}) =>
    addTrailingMargin &&
    `
    ${flipOnLTR`
      margin-left: 20px;
    `}
  `}
  margin-bottom: 8px;
`;

type SignUpFormProps = {
  initialValues?: FormValues;
  onSubmit: (values: FormValues) => void;
  sendActivationCodeError?: string;
  existingEmailAction?: (email: string) => void;
  borderedInputs?: boolean;
  emailValidationErrorKey?: string;
  title?: string;
  subtitle?: string;
  isFixedEmail?: boolean;
};

const SignUpForm = ({
  initialValues = {fullName: '', email: '', cellPhone: '', agreeToTerms: false, wantPromotion: false},
  onSubmit,
  sendActivationCodeError,
  existingEmailAction,
  borderedInputs,
  title,
  subtitle,
  emailValidationErrorKey,
  isFixedEmail,
}: SignUpFormProps) => {
  const {t} = getLocalizationService();

  const formWrapperRef = useRef<HTMLDivElement | null>(null);
  const focusElementRef = useRef<HTMLButtonElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const emailInUse = useRef<boolean | null>(null);

  const validateEmail = useCallback(
    async (emailAddress: string) => {
      const emptyEmailError = validators.requiredValidator(emailAddress);
      if (emptyEmailError) {
        return emptyEmailError;
      }

      const isFree = await checkFreeEmail(emailAddress);

      if (!isFree) {
        emailInUse.current = true;
        return emailValidationErrorKey || 'we_already_have_an_account_with_this_email';
      }
      emailInUse.current = false;
    },
    [emailValidationErrorKey],
  );

  const onExistingEmailActionClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && !e.target.closest('.existing-user-link')) {
      return;
    }

    if (existingEmailAction && emailInputRef.current) {
      existingEmailAction(emailInputRef.current.value);
    }
  }, [existingEmailAction]);

  useEffect(() => {
    if (initialValues.email) {
      validateEmail(initialValues.email);
    }
  }, [initialValues.email, validateEmail]);

  useAutoFocus({
    domEl: focusElementRef?.current,
  });

  const validateFullName = useCallback(
    (fullName: string) => {
      if (!fullName.trim().includes(' ')) {
        return t('we_havent_seen_such_an_exotic_name_yet');
      }
    },
    [t],
  );

  const validateNewUser = ({agreeToTerms}: {agreeToTerms?: boolean}) => {
    const errors: Record<string, string> = {};

    if (!agreeToTerms) {
      errors.agreeToTerms = 'accepting_the_terms_is_required';
    }

    return errors;
  };

  return (
    <div ref={formWrapperRef}>
      <Form
        onSubmit={onSubmit}
        validate={validateNewUser}
        initialValues={initialValues}
        validateOnBlur
        render={({handleSubmit, errors, touched}) => {
          return (
            <FormWrapper onSubmit={handleSubmit} noValidate alignCenter={!!title}>
              {!title && (
                <HeaderAndSubheaderContainer>
                  <HeaderText id="modal-title">{t('new_to_brandName')}</HeaderText>
                  <SubheaderText>{t('were_know_youre_hungry_so_lets_make_it_quick')}</SubheaderText>
                </HeaderAndSubheaderContainer>
              )}
              {title && (
                <>
                  <BrandingPageTitle>{title}</BrandingPageTitle>
                  {subtitle && (
                    <BrandingPageSubtitle>
                      {subtitle}
                    </BrandingPageSubtitle>
                  )}
                </>
              )}

              <FormContentRoot className="form-content-root">
                <FieldsContainer onClick={onExistingEmailActionClick}>
                  <Row>
                    <FormField
                      {...{
                        name: 'fullName',
                        placeholder: t('full_name'),
                        required: true,
                        requiredErrorText: 'hold_on_whats_your_name',
                        type: 'text',
                        render: FormPieInput,
                        validator: validateFullName,
                        withBorder: !!borderedInputs,
                      }}
                    />
                  </Row>
                  <Row>
                    <FormField
                      {...{
                        name: 'email',
                        placeholder: t('email_3'),
                        type: 'email',
                        required: true,
                        requiredErrorText: 'we_need_an_email_to_get_things_moving',
                        validator: validateEmail,
                        render: FormPieInput,
                        withBorder: !!borderedInputs,
                        readonlyField: isFixedEmail,
                        inputProps: {
                          disabled: isFixedEmail,
                          ref: emailInputRef,
                        },
                      }}
                    />
                  </Row>
                  <Row>
                    <FormField
                      {...{
                        name: 'cellPhone',
                        placeholder: t('cellPhone'),
                        type: 'phone',
                        render: FormPieInput,
                        required: true,
                        requiredErrorText: 'where_should_the_driver_call',
                        withBorder: !!borderedInputs,
                      }}
                    />
                  </Row>
                </FieldsContainer>
                <AgreeToTermsSection
                  onTermsOfUseClick={onTermsOfUseClick}
                  onPrivacyPolicyClick={onPrivacyPolicyClick}
                  errorText={
                    ((touched?.agreeToTerms && errors?.agreeToTerms) || sendActivationCodeError) &&
                    t(errors?.agreeToTerms || sendActivationCodeError)
                  }
                />

                <PieButton ref={focusElementRef} type="submit" variant="primaryLarge">
                  {t('lets_go')}
                </PieButton>
              </FormContentRoot>
            </FormWrapper>
          );
        }}
      />
    </div>
  );
};

export default memo(SignUpForm);
